import {useSortable} from '@dnd-kit/sortable';
import {CSS} from '@dnd-kit/utilities';
import {CSSProperties, MouseEvent as ReactMouseEvent, ReactNode, useEffect, useRef} from 'react';

import styles from './Dashboard.module.css';
import {TileId, TileSpan} from './dashboardModel';
import {tileSpanClassNames} from './Tile';
import {TileEditSurface} from './TileEditSurface';

export const SortableTile = ({
  tileId,
  span,
  onRemove,
  onSetSize,
  children,
}: {
  tileId: TileId;
  span: TileSpan;
  onRemove: (tileId: TileId) => void;
  onSetSize: (tileId: TileId, span: TileSpan) => void;
  children: ReactNode;
}) => {
  const {attributes, listeners, setNodeRef, transform, transition, isDragging} = useSortable({
    id: tileId,
  });
  const suppressClickRef = useRef(false);

  useEffect(() => {
    if (isDragging) {
      suppressClickRef.current = true;
      return;
    }
    if (!suppressClickRef.current) {
      return;
    }
    const timeoutId = window.setTimeout(() => {
      suppressClickRef.current = false;
    }, 0);
    return () => window.clearTimeout(timeoutId);
  }, [isDragging]);

  const style: CSSProperties = {
    // Translate only (no scale): tiles slide into the previewed position as you
    // drag without stretching to match a differently sized neighbor's rect.
    transform: CSS.Translate.toString(transform),
    transition,
  };

  const handleClickCapture = (event: ReactMouseEvent<HTMLDivElement>) => {
    if (!suppressClickRef.current) {
      return;
    }
    event.preventDefault();
    event.stopPropagation();
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={[
        tileSpanClassNames(span),
        styles.tileEditSortable,
        isDragging ? styles.tileEditPlaceholder : '',
      ]
        .filter(Boolean)
        .join(' ')}
      data-dashboard-sortable-id={tileId}
      onClickCapture={handleClickCapture}
    >
      {isDragging ? null : (
        <TileEditSurface
          tileId={tileId}
          span={span}
          onSetSize={onSetSize}
          onRemove={onRemove}
          dragHandleProps={{attributes, listeners}}
        >
          {children}
        </TileEditSurface>
      )}
    </div>
  );
};
